import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { Button, CollapsibleSection, FontDropdown, Toggle } from '../common';
import { useMemeStore, useUIStore } from '../../stores';
import { MEME_FONTS } from '../../constants';
import { playClickSound } from '../../utils/gameSound';
import { isBackgroundLayer, isFrameLayer } from '../../utils/layerUtils';

// Trash can icon component
function TrashIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

// Plus icon component
function PlusIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function DragHandleIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <circle cx="6" cy="5.5" r="1.25" />
      <circle cx="6" cy="10" r="1.25" />
      <circle cx="6" cy="14.5" r="1.25" />
      <circle cx="14" cy="5.5" r="1.25" />
      <circle cx="14" cy="10" r="1.25" />
      <circle cx="14" cy="14.5" r="1.25" />
    </svg>
  );
}

function BackgroundIcon({ className = '', active = false }: { className?: string; active?: boolean }) {
  return (
    <svg className={className} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 15l4-4 3 3 4-4 7 7" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function MemeSidebar() {
  const bgInputRef = useRef<HTMLInputElement>(null);
  const textPropertiesRef = useRef<HTMLDivElement>(null);
  const { openModal } = useUIStore();

  const {
    nftElements,
    textElements,
    selectedElementId,
    selectedElementType,
    selectElement,
    removeNFTElement,
    bringToFront,
    sendToBack,
    updateNFTElementScale,
    updateNFTElementRotation,
    updateNFTElementLayers,
    updateNFTElementStickerMode,
    reorderNFTElements,

    removeTextElement,
    updateTextElement,
    addTextElement,
    backgroundImage,
    backgroundImageScale,
    backgroundColor,
    setBackgroundImage,
    setBackgroundImageScale,
    setBackgroundImageOffset,
    setBackgroundColor,
    canvasWidth,
    canvasHeight,
    setExclusiveNFTBackground,
  } = useMemeStore();
  const [draggingNFTId, setDraggingNFTId] = useState<string | null>(null);
  const [dragOverNFTId, setDragOverNFTId] = useState<string | null>(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const [mobileTextDraft, setMobileTextDraft] = useState('');
  const [keyboardInset, setKeyboardInset] = useState(0);

  const handleBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;

      // Load image to get dimensions for auto-fit
      const img = new Image();
      img.onload = () => {
        setBackgroundImage(dataUrl, { width: img.width, height: img.height });
      };
      img.onerror = () => {
        // Fallback without auto-fit if image load fails
        setBackgroundImage(dataUrl);
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);

    e.target.value = '';
  };

  // Clear background image
  const handleClearBackground = () => {
    setBackgroundImage(null);
  };

  // Reset background image position and scale
  const handleResetBackgroundPosition = () => {
    setBackgroundImageOffset({ x: 0, y: 0 });
    // Re-calculate auto-fit scale
    if (backgroundImage) {
      const img = new Image();
      img.onload = () => {
        const imgAspect = img.width / img.height;
        const canvasAspect = canvasWidth / canvasHeight;
        let scale: number;
        if (imgAspect > canvasAspect) {
          scale = canvasHeight / img.height;
        } else {
          scale = canvasWidth / img.width;
        }
        setBackgroundImageScale(scale);
      };
      img.src = backgroundImage;
    }
  };

  const handleAddNFT = () => {
    playClickSound();
    openModal('memeSearch');
  };

  const handleAddText = () => {
    playClickSound();
    addTextElement('');
  };

  const selectedNFT = selectedElementType === 'nft'
    ? nftElements.find((e) => e.id === selectedElementId)
    : null;

  const selectedText = selectedElementType === 'text'
    ? textElements.find((e) => e.id === selectedElementId)
    : null;

  const hasMemeBackgroundLayers = selectedNFT?.nftData.layerAssets.some(isBackgroundLayer) ?? false;

  const hasMemeFrameLayers = selectedNFT?.nftData.layerAssets.some(isFrameLayer) ?? false;

  const sortedNFTElements = useMemo(
    () => [...nftElements].sort((a, b) => b.zIndex - a.zIndex),
    [nftElements]
  );

  const clearNFTDragState = useCallback(() => {
    setDraggingNFTId(null);
    setDragOverNFTId(null);
  }, []);

  const handleNFTDragStart = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>, elementId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setDraggingNFTId(elementId);
      setDragOverNFTId(elementId);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    []
  );

  const handleNFTDragMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!draggingNFTId) return;
      const target = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)
        ?.closest('[data-nft-id]') as HTMLElement | null;
      const targetId = target?.dataset.nftId;
      if (!targetId || targetId === draggingNFTId) return;
      setDragOverNFTId(targetId);
      reorderNFTElements(draggingNFTId, targetId);
    },
    [draggingNFTId, reorderNFTElements]
  );

  const handleNFTDragEnd = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      clearNFTDragState();
    },
    [clearNFTDragState]
  );

  const hasCharacterBackgroundLayer = useCallback((elementId: string) => {
    const element = nftElements.find((e) => e.id === elementId);
    if (!element) return false;
    return element.nftData.layerAssets.some(isBackgroundLayer);
  }, [nftElements]);

  const handleExclusiveBackgroundToggle = useCallback((elementId: string, enabled: boolean) => {
    setExclusiveNFTBackground(elementId, enabled);
  }, [setExclusiveNFTBackground]);

  const enforceLtrInput = useCallback((input: HTMLInputElement) => {
    input.setAttribute('dir', 'ltr');
    input.style.setProperty('direction', 'ltr', 'important');
    input.style.setProperty('unicode-bidi', 'normal', 'important');
    input.style.setProperty('text-align', 'left', 'important');
    input.style.setProperty('writing-mode', 'horizontal-tb', 'important');
  }, []);

  const handleContentInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    if (!selectedText) return;
    const input = e.currentTarget;
    enforceLtrInput(input);
    updateTextElement(selectedText.id, { text: input.value });
  }, [selectedText, updateTextElement, enforceLtrInput]);

  const handleOpenMobileTextModal = useCallback(() => {
    if (!selectedText) return;
    setMobileTextDraft(selectedText.text);
    setIsTextModalOpen(true);
  }, [selectedText, updateTextElement]);

  const handleSaveMobileText = useCallback(() => {
    if (!selectedText) return;
    updateTextElement(selectedText.id, { text: mobileTextDraft });
    setIsTextModalOpen(false);
  }, [selectedText, mobileTextDraft, updateTextElement]);

  useEffect(() => {
    const onResize = () => setIsNarrowViewport(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const updateInset = () => {
      const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      setKeyboardInset(inset);
    };
    updateInset();
    viewport.addEventListener('resize', updateInset);
    viewport.addEventListener('scroll', updateInset);
    return () => {
      viewport.removeEventListener('resize', updateInset);
      viewport.removeEventListener('scroll', updateInset);
    };
  }, []);

  useEffect(() => {
    const hasCharacterBackground = nftElements.some((element) => element.useBackgroundLayer);
    const targetColor = !backgroundImage && !hasCharacterBackground ? '#ffffff' : '#1a1a2e';
    if (backgroundColor !== targetColor) {
      setBackgroundColor(targetColor);
    }
  }, [backgroundColor, backgroundImage, nftElements, setBackgroundColor]);

  // Auto-scroll to text properties when a text element is selected
  useEffect(() => {
    if (selectedText && textPropertiesRef.current) {
      // Small delay to allow the CollapsibleSection to open
      setTimeout(() => {
        textPropertiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [selectedText?.id]);

  return (
    <div className="w-full h-full bg-white dark:bg-neutral-900 rounded-xl border border-surface-border dark:border-neutral-800 shadow-sm max-h-[450px] lg:max-h-[calc(100dvh-200px)] overflow-y-auto overscroll-contain touch-pan-y">
      <div className="px-4 pb-4">
        {/* Background Section */}
        <CollapsibleSection title="Background">
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                ref={bgInputRef}
                type="file"
                accept="image/*"
                onChange={handleBackgroundUpload}
                className="hidden"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => bgInputRef.current?.click()}
                className="flex-1 text-xs"
              >
                Upload BG
              </Button>
              {backgroundImage && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearBackground}
                  className="text-accent-error hover:bg-accent-error/10 text-xs"
                >
                  Clear
                </Button>
              )}
            </div>

            {backgroundImage && (
              <div className="space-y-3 pt-2">
                <div className="relative rounded-lg overflow-hidden border border-surface-border dark:border-neutral-800">
                  <img
                    src={backgroundImage}
                    alt="Background preview"
                    className="w-full h-20 object-cover"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-text-muted dark:text-neutral-500 mb-1.5">
                    Zoom: {Math.round(backgroundImageScale * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="5"
                    step="0.02"
                    value={backgroundImageScale}
                    onChange={(e) => setBackgroundImageScale(parseFloat(e.target.value))}
                    className="w-full h-1.5 bg-surface-secondary dark:bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-game-blue"
                  />
                </div>

                <div className="p-2.5 bg-surface-secondary dark:bg-neutral-800/50 rounded-lg">
                  <p className="text-[10px] text-text-muted dark:text-neutral-500 mb-2 leading-tight">
                    Drag image to reposition, scroll to zoom
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleResetBackgroundPosition}
                    className="w-full text-[10px] h-7"
                  >
                    Reset Position
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Nub Section */}
        <CollapsibleSection title="Nub">
          <div className="space-y-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleAddNFT}
              leftIcon={<PlusIcon className="w-4 h-4" />}
              className="w-full h-11 border-dashed text-xs font-semibold tracking-wide text-text-muted dark:text-neutral-400 hover:text-game-blue"
            >
              Search / Add Nub
            </Button>

            {nftElements.length > 0 ? (
              <div className="space-y-2">
                {sortedNFTElements.map((element) => (
                  <div
                    key={element.id}
                    data-nft-id={element.id}
                    onClick={() => {
                      if (draggingNFTId) return;
                      selectElement(element.id, 'nft');
                    }}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all border ${
                      draggingNFTId === element.id
                        ? 'opacity-70 border-game-blue/70 bg-game-blue/10'
                        : dragOverNFTId === element.id
                          ? 'border-game-blue/60 bg-game-blue/5'
                          : ''
                    } ${
                      selectedElementId === element.id
                        ? 'bg-game-blue/10 border-game-blue shadow-sm'
                        : 'bg-surface-secondary dark:bg-neutral-800/30 border-transparent hover:border-surface-border dark:hover:border-neutral-700'
                    }`}
                  >
                    <img
                      src={element.nftData.imageUrl}
                      alt={element.nftData.name}
                      className="w-9 h-9 rounded object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-text-dark dark:text-neutral-200 truncate">
                        {element.nftData.name}
                      </p>
                      <p className="text-[10px] text-text-muted dark:text-neutral-500 font-medium uppercase tracking-tighter">
                        z: {element.zIndex} | scale: {element.scale.toFixed(2)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExclusiveBackgroundToggle(element.id, !element.useBackgroundLayer);
                      }}
                      disabled={!hasCharacterBackgroundLayer(element.id)}
                      className={`p-1.5 rounded-lg transition-all duration-200 ${
                        hasCharacterBackgroundLayer(element.id)
                          ? element.useBackgroundLayer
                            ? 'text-game-blue bg-game-blue/15 border border-game-blue/30 hover:bg-game-blue/20'
                            : 'text-text-muted/60 border border-transparent hover:text-text-dark dark:hover:text-neutral-200 hover:bg-white/80 dark:hover:bg-neutral-800/80 hover:border-surface-border dark:hover:border-neutral-700'
                          : 'text-text-muted/30 cursor-not-allowed border border-transparent'
                      }`}
                      title={
                        !hasCharacterBackgroundLayer(element.id)
                          ? 'No background layer available'
                          : element.useBackgroundLayer
                            ? 'Disable background'
                            : 'Enable background'
                      }
                      aria-label={`${element.useBackgroundLayer ? 'Disable' : 'Enable'} background for ${element.nftData.name}`}
                    >
                      <BackgroundIcon className="w-3.5 h-3.5" active={element.useBackgroundLayer} />
                    </button>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      onPointerDown={(e) => handleNFTDragStart(e, element.id)}
                      onPointerMove={handleNFTDragMove}
                      onPointerUp={handleNFTDragEnd}
                      onPointerCancel={handleNFTDragEnd}
                      className="p-1.5 rounded-lg border border-transparent text-text-muted/60 hover:text-text-dark dark:hover:text-neutral-200 hover:bg-white/80 dark:hover:bg-neutral-800/80 hover:border-surface-border dark:hover:border-neutral-700 transition-all duration-200 cursor-grab active:cursor-grabbing touch-none"
                      title="Drag to reorder"
                      aria-label={`Drag to reorder ${element.nftData.name}`}
                    >
                      <DragHandleIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeNFTElement(element.id);
                      }}
                      className="p-1.5 rounded-lg border border-transparent text-text-muted/50 hover:text-accent-error hover:bg-accent-error/10 hover:border-accent-error/30 transition-all duration-200"
                      title="Remove Nub"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-text-muted dark:text-neutral-600 text-center py-2 uppercase tracking-widest font-bold">
                No nubs yet
              </p>
            )}

            {selectedNFT && (
              <div className="pt-2 border-t border-surface-border dark:border-neutral-800 space-y-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted dark:text-neutral-500">
                  Editing: {selectedNFT.nftData.name}
                </p>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-text-muted dark:text-neutral-500 mb-1.5">
                    Scale: {Math.round(selectedNFT.scale * 50)}
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.02"
                    value={selectedNFT.scale}
                    onChange={(e) =>
                      updateNFTElementScale(selectedNFT.id, parseFloat(e.target.value))
                    }
                    className="w-full h-1.5 bg-surface-secondary dark:bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-game-blue"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-text-muted dark:text-neutral-500 mb-1.5">
                    Rotation: {selectedNFT.rotation}°
                  </label>
                  <input
                    type="range"
                    min="-180"
                    max="180"
                    step="5"
                    value={selectedNFT.rotation}
                    onChange={(e) =>
                      updateNFTElementRotation(selectedNFT.id, parseInt(e.target.value))
                    }
                    className="w-full h-1.5 bg-surface-secondary dark:bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-game-blue"
                  />
                </div>

                {/* Sticker Mode Toggle */}
                {(hasMemeBackgroundLayers || hasMemeFrameLayers) && (
                  <div className="p-3 bg-gradient-to-r from-pink-500/10 to-game-blue/10 rounded-xl border border-pink-500/30">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-text-dark dark:text-neutral-200">Sticker Mode</span>
                        <p className="text-[9px] text-text-muted dark:text-neutral-500 mt-0.5">Remove background & frame</p>
                      </div>
                      <Toggle
                        checked={selectedNFT.stickerMode}
                        onChange={(checked) => updateNFTElementStickerMode(selectedNFT.id, checked)}
                      />
                    </div>
                  </div>
                )}

                {/* Frame Toggle */}
                {hasMemeFrameLayers && !selectedNFT.stickerMode && (
                  <div className="p-3 bg-surface-secondary dark:bg-neutral-800/50 rounded-xl border border-surface-border dark:border-neutral-800">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-text-dark dark:text-neutral-200">Frame</span>
                        <p className="text-[9px] text-text-muted dark:text-neutral-500 mt-0.5">Toggle NFT frame</p>
                      </div>
                      <Toggle
                        checked={selectedNFT.useFrameLayer}
                        onChange={(checked) =>
                          updateNFTElementLayers(
                            selectedNFT.id,
                            selectedNFT.selectedLayers,
                            selectedNFT.useBackgroundLayer,
                            checked
                          )
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Text Section */}
        <CollapsibleSection title="Text">
          <div className="space-y-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleAddText}
              leftIcon={<PlusIcon className="w-4 h-4" />}
              className="w-full h-11 border-dashed text-xs font-semibold tracking-wide text-text-muted dark:text-neutral-400 hover:text-game-blue"
            >
              Add Text
            </Button>

            {textElements.length > 0 ? (
              <div className="space-y-2">
                {/* Text Elements */}
                {textElements.map((element) => (
                  <div
                    key={element.id}
                    onClick={() => selectElement(element.id, 'text')}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all border ${
                      selectedElementId === element.id
                        ? 'bg-game-blue/10 border-game-blue shadow-sm'
                        : 'bg-surface-secondary dark:bg-neutral-800/30 border-transparent hover:border-surface-border dark:hover:border-neutral-700'
                    }`}
                  >
                    <div className="w-9 h-9 rounded bg-surface dark:bg-neutral-700 flex items-center justify-center border border-surface-border dark:border-neutral-600">
                      <span className="text-sm font-black text-text-muted dark:text-neutral-400">T</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-text-dark dark:text-neutral-200 truncate">
                        {element.text}
                      </p>
                      <p className="text-[10px] text-text-muted dark:text-neutral-500 font-medium uppercase tracking-tighter">Text</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTextElement(element.id);
                      }}
                      className="p-1.5 rounded-lg border border-transparent text-text-muted/50 hover:text-accent-error hover:bg-accent-error/10 hover:border-accent-error/30 transition-all duration-200"
                      title="Remove text"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-text-muted dark:text-neutral-600 text-center py-4 uppercase tracking-widest font-bold">
                No text layers yet
              </p>
            )}

            {selectedText && (
              <div ref={textPropertiesRef} className="pt-2 border-t border-surface-border dark:border-neutral-800">
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted dark:text-neutral-500 mb-3">
                  Text Settings
                </p>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-text-muted dark:text-neutral-500 mb-1.5">Content</label>
                {isNarrowViewport ? (
                  <button
                    type="button"
                    onClick={handleOpenMobileTextModal}
                    className="w-full px-3 py-2 text-left bg-white dark:bg-neutral-800 border border-surface-border dark:border-neutral-800 rounded-lg text-sm text-text-dark dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
                  >
                    {selectedText.text || 'Tap to edit text'}
                  </button>
                ) : (
                  <input
                    key={selectedText.id}
                    type="text"
                    dir="ltr"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    defaultValue={selectedText.text}
                    placeholder="YOUR TEXT HERE"
                    onFocus={(e) => enforceLtrInput(e.currentTarget)}
                    onInput={handleContentInput}
                    className="meme-text-input w-full px-3 py-2 text-left bg-white dark:bg-neutral-800 border border-surface-border dark:border-neutral-800 rounded-lg text-sm text-text-dark dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
                  />
                )}
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-text-muted dark:text-neutral-500 mb-1.5">Font Family</label>
                <FontDropdown
                  value={selectedText.fontFamily}
                  options={MEME_FONTS}
                  onChange={(font) => updateTextElement(selectedText.id, { fontFamily: font })}
                  buttonClassName="bg-white dark:bg-neutral-800 border border-surface-border dark:border-neutral-800 text-text-dark dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
                  menuClassName="bg-white dark:bg-neutral-800 border-surface-border dark:border-neutral-700 text-text-dark dark:text-neutral-200"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-text-muted dark:text-neutral-500 mb-1.5">
                  Font Size: {selectedText.fontSize}px
                </label>
                <input
                  type="range"
                  min="12"
                  max="200"
                  step="2"
                  value={selectedText.fontSize}
                  onChange={(e) =>
                    updateTextElement(selectedText.id, { fontSize: parseInt(e.target.value) })
                  }
                  className="w-full h-1.5 bg-surface-secondary dark:bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-game-blue"
                />
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-text-muted dark:text-neutral-500 mb-1.5">Fill</label>
                  <input
                    type="color"
                    value={selectedText.color}
                    onChange={(e) => updateTextElement(selectedText.id, { color: e.target.value })}
                    className="w-full h-8 rounded border border-surface-border dark:border-neutral-800 cursor-pointer overflow-hidden"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-text-muted dark:text-neutral-500 mb-1.5">Stroke</label>
                  <input
                    type="color"
                    value={selectedText.strokeColor}
                    onChange={(e) => updateTextElement(selectedText.id, { strokeColor: e.target.value })}
                    className="w-full h-8 rounded border border-surface-border dark:border-neutral-800 cursor-pointer overflow-hidden"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-text-muted dark:text-neutral-500 mb-1.5">
                  Stroke Width: {selectedText.strokeWidth}
                </label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="1"
                  value={selectedText.strokeWidth}
                  onChange={(e) =>
                    updateTextElement(selectedText.id, { strokeWidth: parseInt(e.target.value) })
                  }
                  className="w-full h-1.5 bg-surface-secondary dark:bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-game-blue"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-text-muted dark:text-neutral-500 mb-1.5">
                  Rotation: {selectedText.rotation}°
                </label>
                <input
                  type="range"
                  min="-180"
                  max="180"
                  step="5"
                  value={selectedText.rotation}
                  onChange={(e) =>
                    updateTextElement(selectedText.id, { rotation: parseInt(e.target.value) })
                  }
                  className="w-full h-1.5 bg-surface-secondary dark:bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-game-blue"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => bringToFront(selectedText.id, 'text')}
                  className="flex-1 text-[10px]"
                >
                  Bring Front
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => sendToBack(selectedText.id, 'text')}
                  className="flex-1 text-[10px]"
                >
                  Send Back
                </Button>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeTextElement(selectedText.id)}
                className="w-full text-[10px] text-accent-error hover:bg-accent-error/10"
              >
                Delete Text
              </Button>
            </div>
              </div>
            )}
          </div>
        </CollapsibleSection>
      </div>

      {isTextModalOpen && selectedText && (
        <div className="fixed inset-0 z-[120]">
          <button
            type="button"
            aria-label="Close text editor"
            className="absolute inset-0 bg-black/45"
            onClick={() => setIsTextModalOpen(false)}
          />
          <div
            className="absolute left-3 right-3 rounded-2xl border border-surface-border dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-2xl p-4"
            style={{ bottom: `${Math.max(12, keyboardInset + 12)}px` }}
          >
            <label className="block text-[10px] font-bold uppercase tracking-wider text-text-muted dark:text-neutral-500 mb-2">
              Edit Text
            </label>
            <input
              type="text"
              dir="ltr"
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={mobileTextDraft}
              onFocus={(e) => enforceLtrInput(e.currentTarget)}
              onChange={(e) => setMobileTextDraft(e.target.value)}
              className="meme-text-input w-full px-3 py-2 text-left bg-white dark:bg-neutral-800 border border-surface-border dark:border-neutral-700 rounded-lg text-sm text-text-dark dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-game-blue/50"
            />
            <div className="mt-3 flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsTextModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveMobileText}
                className="flex-1"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
